<template>
  <v-col cols="12" sm="4" md="3" lg="3">
    <v-hover v-slot:default="{ hover }">
      <v-card
        class="scenario-block hoverable"
        :class="{duplicated: duplicated}"
        style="position: relative"
        max-height="350"
        min-height="350"
      >
        <!-- Scenario efficiency Tag -->
        <div v-if="scenario.hasEnergyEfficiency" class="scenario-energy-efficiency">
          <FontIcon class="scen-energy-icon">energy_efficiency</FontIcon>
        </div>
        <!-- :class="{ 'on-hover': hover }" -->
        <!-- Scenario HEADER -->
        <v-list-item two-line>
            <v-list-item-title class="mb-1">
              <div class="mt-3 mb-2 scenario-date">{{ scenarioDateString }}</div>
              <span class="scenario-title">{{ scenario.name }}</span>
           </v-list-item-title>

        </v-list-item>
        <!-- SCENARIO IMAGE -->
        <div class="scenario-configuration-div">
          <img :src="`/img/configurations/${scenario.configuration}.png`" />
        </div>

        <v-card-text class="scenario-card-text">
          <div class="subtext">{{ scenarioConfigurationName }}</div>
        </v-card-text>

        <v-fade-transition>
          <v-overlay
            v-if="hover"
            style="cursor: pointer"
            @click.native.stop="loadScenario"
            absolute
            color="#0152F5"
            opacity=".8"
          >
            <v-container fluid>
              <!-- TODO : configuration for tools -->
              <v-row justify="center">
                <v-col cols="12">
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <v-btn
                        v-on="on"
                        icon
                        class="mr-6"
                        @click.stop="goToReport('/scenario_executive_report')"
                      >
                        <FontIcon>mdi-eye</FontIcon>
                      </v-btn>
                    </template>
                    <span>{{$t('executive_report') | capitalize}}</span>
                  </v-tooltip>

                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <v-btn v-on="on" icon @click.stop="goToReport('/scenario_technical_report')" class="mr-6">
                        <FontIcon>mdi-grid</FontIcon>
                      </v-btn>
                    </template>
                    <span>{{$t('reports') | capitalize}}</span>
                  </v-tooltip>

                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <v-btn v-on="on" icon @click.stop="duplicate">
                        <FontIcon>mdi-content-duplicate</FontIcon>
                      </v-btn>
                    </template>
                    <span>{{$t('clone') | capitalize}}</span>
                  </v-tooltip>
                </v-col>
              </v-row>
              <v-row justify="center">
                <v-col cols="12">
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <v-btn
                        v-on="on"
                        icon
                        class="mr-6"
                        @click.stop="deleteScenario(scenario.uuid)"
                      >
                        <FontIcon>mdi-delete</FontIcon>
                      </v-btn>
                    </template>
                    <span>{{$t('delete_action') | capitalize}}</span>
                  </v-tooltip>

                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <v-btn v-on="on" icon class="mr-6" @click.stop="loadScenario">
                        <FontIcon>mdi-pencil</FontIcon>
                      </v-btn>
                    </template>
                    <span>{{$t('edit') | capitalize}}</span>
                  </v-tooltip>
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <v-btn v-on="on" icon class="mr-2" @click.stop="openSharingDialog">
                        <FontIcon>mdi-share</FontIcon>
                      </v-btn>
                    </template>
                    <span>{{$t('share') | capitalize}}</span>
                  </v-tooltip>
                </v-col>
              </v-row>
            </v-container>
          </v-overlay>
        </v-fade-transition>
        <v-progress-linear absolute bottom :height="scenario.report ? 45 : 10 " :value="sizingProgress ? sizingProgress.progress : 100" :color="getBarColor()">
          <template v-if="sizingReportInfo">
          <div class="progress-info-1">
           {{ $t(`${sizingReportInfo.realIRR.key}.label`) }}
            <span class="subtitle-desc mr-1"> {{ $t(`${sizingReportInfo.realIRR.key}.subLabel`) }} </span>
            {{ (sizingReportInfo.realIRR.value * 100).toFixed(2) }}
            <span class="subtitle-desc">{{ sizingReportInfo.realIRR.unit }}</span>
          </div>
          <div class="progress-info-2">
            <div class="co2-icon mr-1"></div>
            {{ sizingReportInfo.co2Saved.value }}
            <span class="subtitle-desc"> {{ $t(`units.${sizingReportInfo.co2Saved.unit}`) }}</span>
          </div>
          </template>
        </v-progress-linear>
      </v-card>
    </v-hover>
    <SharingDialog :hasBeenShared="getScenarioShared" :invalidMails="getInvalidMails" @send-scenario="sendScenario" @close-dialog="closeSharingDial" :dialogProp="dialog" />
  </v-col>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import _scenario from '@/helpers/scenarioHelper';
import FontIcon from '@/components/FontIcon.vue';
import { ScenarioMeta } from '@/classes/ScenarioMeta';
import { EventBus, EventBusEvents } from '@/helpers/event-bus';
import SharingDialog from '@/components/SharingDialog.vue';
import { SizingProgressBar } from '@/interfaces/PayloadsType';
import { ProjectMeta } from '@/classes/ProjectMeta';
import { ProjectTypeRef } from '@/dto/scenario/ProjectReference';

@Component({
  filters: {
    capitalize: function(value: any) {
      if (!value) return '';
      value = value.toString();
      return value.charAt(0).toUpperCase() + value.slice(1);
    }
  },
  components: {
    FontIcon,
    SharingDialog
  }
})
export default class ProjectScenario extends Vue {
  @Prop({ required: true })
  public scenario!: ScenarioMeta;

  @Prop({ required: true })
  public project!: ProjectMeta;

  @Prop({ required: false, default: () => false })
  public duplicated: boolean | undefined;

  private dialog: boolean = false;

  get sizingProgress(): SizingProgressBar {
    if (!this.scenario.sizing) {
      return {
        progress: 100,
        success: null
      };
    }
    return {
      progress:
        this.scenario.sizing.progress === undefined || this.scenario.sizing.progress === null
          ? 100
          : this.scenario.sizing.progress * 100,
      success: this.scenario.sizing.success
    };
  }

  get sizingReportInfo(): any {
    if (!this.scenario.report) {
      return undefined;
    }
    return {
      co2Saved: this.scenario.report && this.scenario.report.co2Saved,
      realIRR: this.scenario.report && this.scenario.report.realIRR
    };
  }

  get sizingReportCo2(): any {
    if (!this.sizingReportInfo.co2Saved) {
      return undefined;
    }
    return this.sizingReportInfo.co2Saved;
  }
  get scenarioDateString(): string {
    return (this.scenario.updatedAt || new Date()).toLocaleString(
      this.$i18n.locale
    );
  }

  get scenarioConfigurationName(): string {
    const configurationIsValid = _scenario.validConfigurations.find(
      (c) => c === this.scenario.configuration
    );
    return this.$t(
      `configurations.${
        configurationIsValid
          ? this.scenario.configuration
          : 'invalid_configuration'
      }`
    ).toString();
  }

  get getInvalidMails() {
    return this.$store.getters['scenario/getInvalidMails'];
  }

  get getScenarioShared() {
    return this.$store.getters['scenario/getScenarioShared'];
  }

  private goToReport(path: string) {
    if (this.project.typeRef === ProjectTypeRef.ren_fr_pv_project || this.project.typeRef === ProjectTypeRef.ren_fr_wind_project) {
      // this.$router.push(`/ren-france${path}`);
      // Temporary adjustment for Ren France scenario_executive_report routing
      if (path === '/scenario_executive_report') {
        return;
      }
      if (!this.scenario.report) {
        return;
      }
      this.$store.dispatch('scenario/loadScenario', this.scenario.uuid)
        .then(() => {
          this.$store.dispatch('scenario/loadAlgoByScenarioId', this.scenario.uuid).then(
            () => {
              this.$router.push('/scenario_ren_fr_economical_report');
            }
          );
        });
    } else {
      if (!this.scenario.report) {
        return;
      }
      this.$store.dispatch('scenario/loadScenario', this.scenario.uuid)
        .then(() => {
          this.$store.dispatch('scenario/loadAlgoByScenarioId', this.scenario.uuid).then(
            () => {
              this.$router.push(path);
            }
          );
        });
    }
  }

  private sendScenario(emails: string[]) {
    this.$store.dispatch('scenario/shareScenario', { uuid: this.scenario.uuid, emails });
  }

  private closeSharingDial() {
    this.dialog = false;
    this.$store.dispatch('scenario/setInvalidEmails', null);
  }

  private getBarColor() {
    return _scenario.getBarColor(this.sizingProgress.success);
  }
  private openSharingDialog() {
    this.dialog = true;
  }

  private mounted() {
    this.dialog = false;
  }
  private loadScenario() {
    EventBus.$emit(EventBusEvents.loadScenario, this.scenario.uuid);
  }
  private deleteScenario() {
    EventBus.$emit(EventBusEvents.deleteScenario, this.scenario.uuid);
  }
  private duplicate() {
    EventBus.$emit(EventBusEvents.duplicateScenario, {
      scenario: this.scenario,
      projectId: this.project.uuid
    });
  }
}
</script>

<style lang="stylus">
@import '../../stylus/main.styl';

.v-progress-linear__content {
  display flex;
  justify-content space-between;
  padding: 0px 10px;
  font: 15px Roboto;
  color: white;
  font-weight: bold;
  .unit-desc {
    font-size: 9px;
  }
}
.co2-icon {
  mask-image: url("/public/img/ic-co2.svg");
  width: 18px;
  height: 21px;
  vertical-align: sub;
  display: inline-block;
  mask-size: contain;
  mask-repeat: no-repeat;
  background-color: white;
}
 .subtitle-desc {
    font: 8px Roboto;
    letter-spacing: 0.1;
 }
.progress-info-1{
  flex: 1;
}
.progress-info-2{
  flex: 1;
  text-align end;
}

.scenario-block {
  overflow: hidden;
  // cursor: pointer;
  display: flex;
  flex-direction: column;

  &.duplicated {
    border: 2px solid $blue-background;
  }

  .scenario-date {
    font-size: 12px;
    color: $grey-color;
    opacity: 0.7;
  }

  .scenario-title {
    color: $blue-title;
    font-weight: bold;
    line-height: 2em;
    max-height: 4em;
  }

  .scenario-configuration-div {
    flex-grow: 1;

    img {
      margin-left: auto;
      margin-right: auto;
      display: block;
    }
  }

  .scenario-card-text {
    flex-grow: 1;

    .subtext {
      text-align: center;
      justify-content: space-around;
      font-size: 1em;
      line-height: 1rem;
      max-height: 2rem;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      overflow: hidden;
    }
  }

  .scenario-energy-efficiency {
    position: absolute;
    background-color: #007B47;
    height: 125px;
    right: -50px;
    position: absolute;
    top: -60px;
    width: 100px;
    -webkit-transform: rotate(-45deg);

    .scen-energy-icon {
      position: absolute;
      -webkit-transform: rotate(45deg) translate(50px, 25px);
      color: white;
      right: 90px;
    }
  }
}
</style>
